import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { Profile } from '@playcastdotio/Messaging';
import { SOCIAL_PLATFORMS } from '../constants';

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type SocialLinksModalProps = ModalProps & {
  socialLinkPlatformState: [string, Dispatch<SetStateAction<string>>];
  socialLinkUrlState: [string, Dispatch<SetStateAction<string>>];
  handleSocialLinkSubmit: () => void;
  handleRemoveSocialLink: () => void;
  editedProfile: Profile | null;
};

export const SocialLinksModal = ({
  isOpen,
  onClose,
  socialLinkPlatformState,
  socialLinkUrlState,
  handleSocialLinkSubmit,
  handleRemoveSocialLink,
  editedProfile,
}: SocialLinksModalProps) => {
  const [selectedPlatform, setSelectedPlatform] = socialLinkPlatformState;
  const [url, setUrl] = socialLinkUrlState;
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (selectedPlatform && editedProfile?.socialLinks?.[selectedPlatform]) {
      setUrl(editedProfile.socialLinks[selectedPlatform]);
    } else {
      setUrl('');
    }
    setError('');
  }, [selectedPlatform, editedProfile, setUrl]);

  const ensureHttpsPrefix = useCallback(
    (inputUrl: string): string => {
      // Don't modify email addresses
      if (selectedPlatform === 'email') {
        return inputUrl;
      }

      // Check if the URL starts with http:// or https://
      if (!inputUrl.match(/^https?:\/\//i)) {
        return `https://${inputUrl}`;
      }

      return inputUrl;
    },
    [selectedPlatform],
  );

  const validateUrl = useCallback(
    (urlToValidate: string) => {
      // Clear previous errors
      setError('');

      // If no URL or no platform selected, return false
      if (!urlToValidate.trim() || !selectedPlatform) {
        setError('Please enter a URL and select a platform');
        return false;
      }

      // Special case for email
      if (selectedPlatform === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(urlToValidate)) {
          setError('Please enter a valid email address');
          return false;
        }
        return true;
      }

      const platform = SOCIAL_PLATFORMS.find((p) => p.id === selectedPlatform);

      // For website and other, just check if it's a valid URL
      if (selectedPlatform === 'website' || selectedPlatform === 'other' || !platform?.validDomains.length) {
        try {
          new URL(urlToValidate);
          return true;
        } catch (e) {
          setError('Please enter a valid URL (including https://)');
          return false;
        }
      }

      // For platforms with specific domains
      try {
        const urlObj = new URL(urlToValidate);
        const domain = urlObj.hostname.toLowerCase();

        const isValidDomain = platform?.validDomains.some((validDomain) => domain === validDomain || domain.endsWith(`.${validDomain}`));

        if (!isValidDomain) {
          setError(`This URL doesn't match ${platform?.name} domains (${platform?.validDomains.join(', ')})`);
          return false;
        }

        return true;
      } catch (e) {
        setError('Please enter a valid URL (including https://)');
        return false;
      }
    },
    [selectedPlatform],
  );

  const handleSubmit = useCallback(() => {
    // First ensure https:// prefix is added if needed (but don't update UI)
    const formattedUrl = ensureHttpsPrefix(url);

    // Use the formatted URL for validation
    if (validateUrl(formattedUrl)) {
      // Update the state silently with the formatted URL before submitting
      if (formattedUrl !== url) {
        socialLinkUrlState[1](formattedUrl);
      }

      handleSocialLinkSubmit();
      onClose();
    }
  }, [handleSocialLinkSubmit, onClose, validateUrl, url, socialLinkUrlState, ensureHttpsPrefix]);

  const handleRemove = useCallback(() => {
    handleRemoveSocialLink();
    onClose();
  }, [handleRemoveSocialLink, onClose]);

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50'>
      <div className='bg-darkest relative w-full max-w-xs rounded-lg p-6 text-center'>
        <button onClick={onClose} className='absolute right-4 top-4 text-white hover:text-gray-300'>
          <FontAwesomeIcon icon={faXmark} className='size-6' />
        </button>
        <h2 className='mb-4 w-full text-left font-[Syne] text-2xl font-semibold text-white'>Edit Social Links</h2>
        <div className='flex flex-col gap-4'>
          <div className='flex flex-col gap-2'>
            <label htmlFor='platform' className='text-left text-sm text-white'>
              Platform
            </label>
            <div className='relative'>
              <select
                id='platform'
                value={selectedPlatform}
                onChange={(e) => {
                  setSelectedPlatform(e.target.value);
                  setError('');
                }}
                className='w-full appearance-none rounded-lg bg-white/10 px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-amber-400'
              >
                <option value='' disabled>
                  Select a platform
                </option>
                {SOCIAL_PLATFORMS.map((platform) => (
                  <option key={platform.id} value={platform.id} className='bg-darkest text-white'>
                    {platform.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className='flex flex-col gap-2'>
            <label htmlFor='url' className='text-left text-sm text-white'>
              URL
            </label>
            <input
              type='url'
              id='url'
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                setError('');
              }}
              placeholder={selectedPlatform === 'email' ? 'email@example.com' : 'https://'}
              className='w-full rounded-lg bg-white/10 px-4 py-2 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-amber-400'
            />
          </div>
        </div>

        {error && <p className='mt-2 text-left text-sm text-red-500'>{error}</p>}

        <div className='flex justify-between gap-4'>
          <button
            className='text-darkest mt-4 w-full rounded-lg bg-amber-400 px-4 py-2 font-[Syne] text-lg font-bold hover:bg-amber-400/80'
            onClick={handleSubmit}
          >
            Apply
          </button>
          <button
            disabled={!editedProfile?.socialLinks?.[selectedPlatform]}
            className='mt-4 w-full rounded-lg bg-red-600 px-4 py-2 font-[Syne] text-lg font-bold text-white hover:bg-red-600/80 disabled:opacity-50 disabled:hover:bg-red-600'
            onClick={handleRemove}
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );
};
